// Brevo (Sendinblue) wiring for the Easy Checkout flow.
//
// Trigger model is TTM's (shared-ui + subscribe-v2.page.jsx), which is the one
// known to be capturing abandoned carts today: Step 3 emits `email-confirmed`
// from the email field's blur AND again from Continue, and this side dedupes
// on the email value. Blur gets the event out early; Continue is the one
// action every path to Step 4 goes through, so it catches autofill, pasted
// emails and anything else that never produced a blur. No timer — the old
// storefront's 3s delay isn't needed once the repeat is deduped.
//
// And a third trigger, the Pay step being shown: anyone looking at Step 4 has
// an email on file, so the event must have gone out for it, however they got
// there. PostHog shows sessions on Step 4 with no event from Step 3 (a reload
// or saved link that opens straight on Pay, and some that opened on Delivery
// and left no click behind), so Step 3 alone can't be the only place it fires.
//
// Event names and contact attribute keys are the MTM storefront's
// (react-app checkout.page.jsx / utils/sib.functions.jsx), because the live
// Brevo automations were built against those exact strings.

// Marketing telemetry must never be able to interrupt a purchase, and these
// calls are made right before navigating to the next step — keepalive lets
// them finish after the page they came from is gone. Same shape as TTM's
// sibBeacon. They go to the core-api Worker's /api/<action> (it replaced
// WordPress's admin-ajax.php for these calls).
function beacon(action, values) {
  try {
    fetch(`/api/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values),
      keepalive: true,
    }).catch(() => {});
  } catch { /* never block checkout on telemetry */ }
}

// sib_update_contact splits `attr` on "," and ":" with no escaping, so a
// comma or colon inside a value would corrupt every attribute after it.
const clean = (v) => String(v == null ? '' : v).replace(/[,:"\\]/g, ' ').trim();

const itemName = (gift) => (gift ? 'MTM Subscription Box - Gift' : 'MTM Subscription Box - NonGift');

// The last email actually handed to Brevo, and the last one counted in
// PostHog. Kept apart on purpose: an email only counts as "sent to Brevo" once
// the tracker really took it, so Continue can still send it if the tracker was
// missing at the first confirmation (blur).
let trackedEmail = '';
let measuredEmail = '';

// The visitor was sent away from a step by the flow's own guard (most often from
// Pay back to Delivery: a pay link opened with no saved details). Same shape as
// TTM's ttm_checkout_guard_redirect: which step to which and why, and booleans for
// what was missing, never the values. PostHog already carries the referrer and
// campaign tags on every event.
const recordBounce = (s) => phCapture('mtm_checkout_guard_redirect', {
  from_step: s.from,
  to_step: s.to,
  reason: s.reason,
  wed_date: s.wedDate || '',
  plan_code: s.planCode || '',
  has_email: !!s.email,
  has_first: !!s.first,
  has_last: !!s.last,
  has_street: !!s.street,
  has_city: !!s.city,
  has_region: !!s.region,
  has_postal: !!s.postal,
});

// PostHog marker with no email in it (PostHog ties events to a person by
// itself). Same guard as TTM's phCapture: telemetry cannot interrupt checkout.
function phCapture(name, props) {
  try {
    if (!window.posthog || typeof window.posthog.capture !== 'function') return;
    window.posthog.capture(name, props);
  } catch { /* never block checkout on telemetry */ }
}

// Abandoned-cart trigger. `window.sendinblue` is the tracker header-react-app
// .php loads (sibautomation.com/sa.js); it arrives async and an ad blocker can
// stop it outright, hence the guard.
function trackCheckoutStarted(s, trigger) {
  if (!s.email) return;

  // Counted in PostHog independently of Brevo, so this milestone can be checked
  // against the pay-step funnel and Brevo's own counts (as TTM's
  // ttm_checkout_started does, 869f6ktjj). brevo_tracker_ready says whether
  // Brevo could even receive it at that moment, which separates "the app
  // didn't fire" from "the tracker was blocked".
  if (s.email !== measuredEmail) {
    measuredEmail = s.email;
    phCapture('mtm_checkout_started', { has_email: true, brevo_tracker_ready: !!window.sendinblue });
  }

  if (s.email === trackedEmail) return;
  if (!window.sendinblue) return; // the next confirmation retries
  try {
    // `window.sendinblue.track` hands the call to Brevo's real tracker
    // (window.sib.track, defined once sa.js has loaded) and otherwise only
    // QUEUES it; the page's own stub is there from the first byte either way, so
    // the object existing says nothing about sa.js. Read before the call.
    const scriptLoaded = !!(window.sib && typeof window.sib.track === 'function');
    window.sendinblue.track(
      'checkout_started',
      { email: s.email },
      {
        data: {
          cart_url: window.location.href,
          products: [{ sku: s.planCode || '', name: itemName(s.audience === 'gift') }],
        },
      },
    );
    trackedEmail = s.email;
    // The same moment, in PostHog, without the email. Fired only when the call
    // above was actually made, once per email; mtm_checkout_started above is the
    // separate "got here" count.
    phCapture('mtm_brevo_checkout_started', {
      plan_code: s.planCode || '',
      audience: s.audience === 'gift' ? 'gift' : 'self',
      trigger, // 'email-confirmed' (blur or Continue) or 'pay-step'
      step: s.step || '',
      brevo_script_loaded: scriptLoaded,
    });
  } catch { /* the next confirmation retries */ }
}

// IP lookup behind the gifter's MTM_GIFT_COUNTRY / MTM_GIFT_ZIP attributes —
// the same ipapi.co call (5s cap) the storefront makes. Only the gift path
// needs it here, so it runs lazily, once.
let geoPromise = null;
function getGeo() {
  if (!geoPromise) {
    geoPromise = (async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      try {
        const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
        return await res.json();
      } catch {
        return null;
      } finally {
        clearTimeout(timer);
      }
    })();
  }
  return geoPromise;
}

let giftTaggedEmail = '';
async function tagGifter(email) {
  if (!email || email === giftTaggedEmail) return;
  giftTaggedEmail = email;
  const geo = await getGeo();
  // The storefront only tags once it knows both; sending "null" as a
  // country or ZIP would overwrite a good value with junk.
  if (!geo || !geo.country_code || !geo.postal) return;
  beacon('sib_update_contact', {
    email,
    attr: `MTM_GIFTER:true,MTM_GIFT_COUNTRY:${clean(geo.country_code)},MTM_GIFT_ZIP:${clean(geo.postal)}`,
  });
}

// Same national-prefix guesses checkout.page.jsx makes for the SMS attribute.
function smsNumber(raw) {
  let phone = (raw || '').trim();
  if (phone && phone.substring(0, 2) !== '00' && phone.substring(0, 1) !== '+') {
    if (phone.length === 11 && phone.substring(0, 1) !== '1') phone = `+44${phone}`;
    else if (phone.length === 10) phone = `+1${phone}`;
    else if (phone.length === 9) phone = `+61${phone}`;
  }
  return phone;
}

// The storefront posts its whole form object as the SMS event's data, so the
// automation can template against any of its keys. Everything Step 3 knows
// is mapped onto those same keys; bill_country / bill_zip / price are still
// empty at this point in the storefront too (payment section not opened yet).
function smsEventData(s, phone) {
  const gift = s.audience === 'gift';
  const bonus = [
    s.bonusCode ? `promo-${s.bonusCode}` : '',
    s.appliedCoupon ? `promo-${s.appliedCoupon.toLowerCase().replace('off', '')}` : '',
  ].filter(Boolean).join(',');
  const [year, month, day] = (s.wedDate || '').split('-');
  return {
    plan: s.planCode,
    addon: s.country === 'CA' ? 'ca-shipping' : 'us-shipping',
    bonus,
    pay_source: '',
    currency: 'USD',
    email: s.email,
    shirt: s.topSize || '',
    shoe: '',
    wed_date: s.wedDate || '',
    wed_year: year || '',
    wed_month: month ? Number(month) : '',
    wed_day: day ? Number(day) : '',
    ship_fname: s.first,
    ship_lname: s.last,
    ship_street: s.street,
    ship_apt: s.apt || '',
    ship_city: s.city,
    ship_country: s.country || 'US',
    ship_state: s.region,
    ship_zip: s.postal,
    ship_phone: phone,
    ship_method: 'standard',
    bill_country: '',
    bill_zip: '',
    code: '',
    applied_code: s.appliedCoupon || '',
    gift: gift ? 'yes' : '',
    personal_msg_checked: !!(s.giftMessage || '').trim(),
    giftee_email: s.giftEmail || '',
    personal_msg: s.giftMessage || '',
    market_consent: false,
    price: '',
  };
}

// Shipping details are entered and valid (Step 3 → Continue).
function updateContactFromDelivery(s) {
  if (!s.email) return;
  const phone = smsNumber(s.phone);
  let attr = `FIRSTNAME:${clean(s.first)},LASTNAME:${clean(s.last)},`;
  attr += `SHIPPING_CITY:${clean(s.city).toUpperCase()},`;
  if (s.region) attr += `SHIPPING_ADMINISTRATIVE_AREA:${clean(s.region).toUpperCase()},`;
  attr += `SHIPPING_REGION_CODE:${clean(s.country || 'US').toUpperCase()},`;
  attr += `SHIPPING_POSTAL_CODE:${clean(s.postal).toUpperCase()}`;
  if (phone) attr += `,SMS:${clean(phone)}`;
  if (s.audience === 'gift') attr += ',MTM_GIFTER:true';
  beacon('sib_update_contact', { email: s.email, attr });
  if (phone) {
    beacon('sib_event', { email: s.email, event: 'checkout_started_mtm_sms', data: smsEventData(s, phone) });
  }
}

// App-level event bridge, passed to <App onEvent>.
export function onFlowEvent(name, state) {
  const s = state || {};
  if (name === 'email-confirmed') {
    trackCheckoutStarted(s, 'email-confirmed');
    if (s.audience === 'gift') tagGifter(s.email);
  } else if (name === 'delivery-complete') {
    updateContactFromDelivery(s);
  } else if (name === 'step-viewed' && s.step === 'pay') {
    // Same once-per-email guard as above, so a customer who came through
    // Continue is not sent twice.
    trackCheckoutStarted(s, 'pay-step');
  } else if (name === 'checkout-guard-redirect') {
    recordBounce(s);
  }
}
